import { axiosChartInstance } from "config";
import { useAxiosMutation } from "hooks";
import { useQueryClient } from "react-query";

type UseCreateAuditCommentsPayload = { comment: string };

type UseCreateAuditCommentsParams = {
  encounterId: string;
  chartCode: string;
  sectionCode: string;
  subsectionCode?: string;
};

function useCreateAuditComments({
  encounterId,
  chartCode,
  sectionCode,
  subsectionCode,
}: UseCreateAuditCommentsParams) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    [
      "createAuditComments",
      { encounterId, chartCode, sectionCode, subsectionCode },
    ],
    (input: UseCreateAuditCommentsPayload) => {
      const endpoint = subsectionCode
        ? `/v1/audits/subsection/comments/${encounterId}/${chartCode}/${sectionCode}/${subsectionCode}`
        : `/v1/audits/section/comments/${encounterId}/${chartCode}/${sectionCode}`;
      return axiosChartInstance.post(endpoint, input);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "AuditComments",
          { encounterId, chartCode, sectionCode, subsectionCode },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSectionIndicator",
          { encounterId, chartCode, sectionCode, validateSubsections: true },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSectionIndicator",
          { encounterId, chartCode, sectionCode, validateSubsections: false },
        ]);
        if (subsectionCode) {
          queryClient.invalidateQueries([
            "AuditCommentsSubsectionIndicator",
            { encounterId, chartCode, sectionCode, subsectionCode },
          ]);
        }
      },
    }
  );
}

type UseCreateAuditCommentsSharedParams = {
  encounterId: string;
  sectionCode: string;
  subsectionCode?: string;
};

function useCreateAuditCommentsShared({
  encounterId,
  sectionCode,
  subsectionCode,
}: UseCreateAuditCommentsSharedParams) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    ["createAuditCommentsShared", { encounterId, sectionCode, subsectionCode }],
    (input: UseCreateAuditCommentsPayload) => {
      const endpoint = subsectionCode
        ? `/v1/audits/subsection/comments/${encounterId}/shared/${sectionCode}/${subsectionCode}`
        : `/v1/audits/section/comments/${encounterId}/shared/${sectionCode}`;
      return axiosChartInstance.post(endpoint, input);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "AuditCommentsShared",
          { encounterId, sectionCode, subsectionCode },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSharedSectionIndicator",
          { encounterId, sectionCode, validateSubsections: true },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSharedSectionIndicator",
          { encounterId, sectionCode, validateSubsections: false },
        ]);
        if (subsectionCode) {
          queryClient.invalidateQueries([
            "AuditCommentsSharedSubsectionIndicator",
            { encounterId, sectionCode, subsectionCode },
          ]);
        }
      },
    }
  );
}

export type { UseCreateAuditCommentsPayload };
export { useCreateAuditComments, useCreateAuditCommentsShared };
